"use client";

import { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { AlertTriangle, Check, Pencil, Printer, RotateCcw, Share2, Sparkles } from "lucide-react";
import posthog from "posthog-js";
import { Button, buttonStyles } from "@/ui/components/ui/button";
import { GOAL_STACKS, toStackProfileSlug } from "@/config/peptide-stacks";
import type { QuizResponse } from "@/lib/quiz-scoring";
import { buildQuizStackBuilderHref, mapQuizGoalToStackGoal } from "@/lib/quiz-stack-handoff";
import quizQuestions from "@/data/quiz-questions.json";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import { QuizRecommendationCard } from "./quiz-recommendation-card";

interface Props {
	results: QuizResponse;
	answers: Record<string, string>;
	locale: string;
	channel: string;
	onEditAnswer: (stepIndex: number) => void;
	onRestart: () => void;
}

export function QuizResultsView({ results, answers, locale, channel, onEditAnswer, onRestart }: Props) {
	const t = useTranslations("quiz");
	const { recommendations, warnings } = results;
	const [shareCopied, setShareCopied] = useState(false);

	const buildShareUrl = useCallback(() => {
		const params = new URLSearchParams();
		if (answers.goal) params.set("goal", answers.goal);
		if (answers.priority) params.set("priority", answers.priority);
		if (answers.dosing_frequency) params.set("dose", answers.dosing_frequency);
		const path = `/${locale}/${channel}/quiz`;
		if (typeof window !== "undefined") return `${window.location.origin}${path}?${params.toString()}`;
		return `${path}?${params.toString()}`;
	}, [answers, locale, channel]);

	const onShare = useCallback(async () => {
		const url = buildShareUrl();
		posthog.capture("quiz_share_clicked");
		try {
			if (navigator.share) {
				await navigator.share({ url });
				return;
			}
		} catch {
			// User cancelled native share — fall through to clipboard copy.
		}
		try {
			await navigator.clipboard.writeText(url);
			setShareCopied(true);
			window.setTimeout(() => setShareCopied(false), 2000);
		} catch {
			// Clipboard API unavailable — no-op. Users can copy the URL bar manually.
		}
	}, [buildShareUrl]);

	const onPrint = useCallback(() => {
		posthog.capture("quiz_pdf_clicked");
		if (typeof window !== "undefined") window.print();
	}, []);

	const priceItems = useMemo(
		() =>
			recommendations
				.filter((r) => r.peptide.priceFrom && r.peptide.priceFrom.amount > 0)
				.map((r) => ({
					id: r.peptide.slug,
					amount: r.peptide.priceFrom!.amount,
					currency: r.peptide.priceFrom!.currency,
				})),
		[recommendations],
	);
	const presMap = usePricingResolvePresentations(locale, channel, priceItems);

	if (recommendations.length === 0) {
		return (
			<div className="rounded-2xl border border-border bg-card p-8 text-center shadow-xs">
				<h2 className="text-lg font-semibold">{t("results.empty.title")}</h2>
				<p className="mt-2 text-sm text-muted-foreground">{t("results.empty.subtitle")}</p>
				<div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
					<Link href={`/${locale}/${channel}/peptides`} className={buttonStyles({ variant: "default" })}>
						{t("viewProduct")}
					</Link>
					<Button variant="outline-solid" onClick={onRestart}>
						<RotateCcw className="mr-2 h-4 w-4" aria-hidden />
						{t("results.actions.restart")}
					</Button>
				</div>
			</div>
		);
	}

	const top = recommendations[0];
	const alternatives = recommendations.slice(1, 3);
	const goalValue = answers.goal ?? "";
	const stackGoal = mapQuizGoalToStackGoal(goalValue);
	const goalStack = stackGoal ? GOAL_STACKS.find((s) => s.goal === stackGoal) : null;
	const topStackSlug = top ? toStackProfileSlug(top.peptide.slug) : null;
	const stackHref = buildQuizStackBuilderHref({
		locale,
		channel,
		rawGoal: goalValue,
		topProfileSlug: topStackSlug,
	});
	const trackStackHandoff = () => {
		posthog.capture("quiz_stack_cta_clicked", {
			raw_goal: goalValue || null,
			mapped_goal: stackGoal,
			top_recommendation_slug: top.peptide.slug,
			selected_profile_slug: topStackSlug,
			channel,
		});
	};

	return (
		<div className="quiz-result space-y-6">
			{/* Print-mode global rules — lab-report clean, navy hero preserved, site chrome hidden. */}
			<style>{`
				@media print {
					@page { size: A4; margin: 1.5cm; }
					html, body { background: white !important; color: black; }
					header, footer, nav,
					[data-quiz-seo],
					.no-print { display: none !important; }
					.quiz-result, .quiz-result * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
				}
			`}</style>
			<header className="text-center">
				<p className="text-xs font-semibold uppercase tracking-wider text-primary">{t("results.eyebrow")}</p>
				<h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
					{t("results.title", { count: recommendations.length })}
				</h2>
				<p className="mt-1 text-sm text-muted-foreground">{t("results.subtitle")}</p>
			</header>

			{/* Editable answer chips */}
			<div
				className="flex flex-wrap justify-center gap-2 border-b border-border pb-4 print:hidden"
				aria-label={t("results.answersLabel")}
			>
				{quizQuestions.map((q, i) => {
					const value = answers[q.slug];
					if (!value) return null;
					const label = t(`questions.${q.slug}.options.${value}.label`);
					const stepLabel = t(`progress.${q.slug === "dosing_frequency" ? "dose" : q.slug}`);
					return (
						<button
							key={q.slug}
							type="button"
							onClick={() => onEditAnswer(i)}
							className="bg-muted/50 inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs transition hover:border-primary hover:bg-muted"
						>
							<span className="text-muted-foreground">{stepLabel}:</span>
							<span className="font-semibold">{label}</span>
							<Pencil className="h-3 w-3 text-muted-foreground" aria-hidden />
							<span className="sr-only">{t("results.editAnswer")}</span>
						</button>
					);
				})}
			</div>

			{/* Contraindications */}
			{warnings.length > 0 && (
				<div className="space-y-3">
					{warnings.map((w) => (
						<div
							key={`${w.peptideA}-${w.peptideB}`}
							className="flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4 text-sm"
						>
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" aria-hidden />
							<div>
								<p className="font-medium">
									{t("warning.contraindication", {
										peptideA:
											recommendations.find((r) => r.peptide.slug === w.peptideA)?.peptide.name ?? w.peptideA,
										peptideB:
											recommendations.find((r) => r.peptide.slug === w.peptideB)?.peptide.name ?? w.peptideB,
									})}
								</p>
								<p className="mt-1 text-muted-foreground">{w.reason}</p>
							</div>
						</div>
					))}
				</div>
			)}

			<div className="print:break-inside-avoid">
				<QuizRecommendationCard
					result={top}
					locale={locale}
					channel={channel}
					position={0}
					variant="hero"
					pricePresentation={presMap.get(top.peptide.slug) ?? null}
				/>
			</div>

			{alternatives.length > 0 && (
				<section aria-labelledby="alts-heading" className="print:break-inside-avoid">
					<h3
						id="alts-heading"
						className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
					>
						{t("results.alternativesLabel")}
					</h3>
					<div className="grid gap-3 sm:grid-cols-2 print:grid-cols-1">
						{alternatives.map((r, i) => (
							<QuizRecommendationCard
								key={r.peptide.slug}
								result={r}
								locale={locale}
								channel={channel}
								position={i + 1}
								variant="alternative"
								pricePresentation={presMap.get(r.peptide.slug) ?? null}
							/>
						))}
					</div>
				</section>
			)}

			<div className="border-primary/20 bg-primary/5 flex flex-wrap items-center gap-3 rounded-xl border p-4 print:hidden">
				<Sparkles className="h-5 w-5 shrink-0 text-primary" aria-hidden />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-semibold">{t("results.stackCta.title")}</p>
					<p className="text-xs text-muted-foreground">{t("results.stackCta.subtitle")}</p>
					{goalStack && (
						<p className="mt-1 text-xs font-medium">
							{goalStack.peptides
								.map(
									(slug) =>
										recommendations.find((r) => r.peptide.slug === slug)?.peptide.name ?? slug.toUpperCase(),
								)
								.join(" + ")}
						</p>
					)}
				</div>
				<Link
					href={stackHref}
					onClick={trackStackHandoff}
					className={buttonStyles({ variant: "default", size: "sm" })}
				>
					{t("results.stackCta.button")}
				</Link>
			</div>

			<footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 text-xs text-muted-foreground print:hidden">
				<div className="flex flex-wrap items-center gap-2">
					<Button variant="ghost" size="sm" onClick={onShare} aria-live="polite">
						{shareCopied ? (
							<>
								<Check className="mr-2 h-3.5 w-3.5 text-primary" aria-hidden />
								{t("results.actions.shareCopied")}
							</>
						) : (
							<>
								<Share2 className="mr-2 h-3.5 w-3.5" aria-hidden />
								{t("results.actions.share")}
							</>
						)}
					</Button>
					<Button variant="ghost" size="sm" onClick={onPrint}>
						<Printer className="mr-2 h-3.5 w-3.5" aria-hidden />
						{t("results.actions.pdf")}
					</Button>
				</div>
				<Button variant="outline-solid" size="sm" onClick={onRestart}>
					<RotateCcw className="mr-2 h-3.5 w-3.5" aria-hidden />
					{t("results.actions.restart")}
				</Button>
			</footer>
		</div>
	);
}
